import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export default function AdminMembershipApplicationDetail() {
  const { user } = useAuth();
  const { id } = useParams();
  const nav = useNavigate();
  const [app, setApp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [outcome, setOutcome] = useState("");
  const [reason, setReason] = useState("");
  const [internalNote, setInternalNote] = useState("");
  const [applicantMessage, setApplicantMessage] = useState("");
  const [conditions, setConditions] = useState("");
  const [rejectionCategory, setRejectionCategory] = useState("eligibility_or_standards");
  const [reapplyDays, setReapplyDays] = useState(365);
  const [infoFieldsRaw, setInfoFieldsRaw] = useState("");
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const [isOwner, setIsOwner] = useState(false);

  const isAdmin = user && (user.role === "admin" || user.is_owner);

  const load = async () => {
    if (!isAdmin) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/membership-applications/${id}`);
      setApp(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load application.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { if (isAdmin) load(); /* eslint-disable-next-line */ }, [id, isAdmin]);
  useEffect(() => { if (isAdmin) api.get("/auth/me").then((r) => setIsOwner(!!r.data?.is_owner)).catch(() => {}); }, [isAdmin]);

  const submitDecision = async () => {
    if (!outcome) return;
    setSaving(true);
    setErr("");
    try {
      const payload = { outcome, reason, internal_note: internalNote, applicant_message: applicantMessage };
      if (outcome === "approve_with_conditions") payload.conditions = conditions;
      if (outcome === "reject") {
        payload.rejection_category = rejectionCategory;
        if (rejectionCategory === "fraud_or_misconduct") payload.reapply_days = parseInt(reapplyDays, 10) || 365;
      }
      if (outcome === "request_info") {
        payload.info_fields = infoFieldsRaw.split(",").map((x) => x.trim()).filter(Boolean);
      }
      await api.post(`/admin/membership-applications/${id}/decision`, payload);
      await load();
      setOutcome("");
      setReason("");
      setInternalNote("");
      setApplicantMessage("");
      setConditions("");
      setInfoFieldsRaw("");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to save decision.");
    } finally {
      setSaving(false);
    }
  };

  const deleteTest = async () => {
    if (!app?.is_test) return;
    if (!window.confirm("Delete this test application? This is irreversible.")) return;
    await api.delete(`/admin/membership-applications/${id}`);
    nav("/admin/membership-applications");
  };

  const sendReminder = async () => {
    if (!window.confirm("Send an info-request reminder email to this painter?")) return;
    try {
      const { data } = await api.post(`/admin/membership-applications/${id}/send-reminder`);
      window.alert(`Reminder sent. Application has been in info-requested state for ${data.days_open} day(s).`);
      await load();
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Failed to send reminder.");
    }
  };

  if (user === null) return <div className="p-10 text-center text-sm text-brand-muted">Loading…</div>;
  if (!isAdmin) {
    return (
      <div data-testid="admin-access-denied" className="max-w-3xl mx-auto px-6 py-20 text-center">
        <h2 className="font-serif text-3xl mb-3">Admin access required</h2>
        <p className="text-brand-muted text-sm mb-6">This area is for ColourMatch staff only.</p>
        <a href="/projects" className="inline-block text-sm border border-brand-teal px-5 py-2 text-brand-teal hover:bg-brand-teal hover:text-white transition-colors">Back to your projects</a>
      </div>
    );
  }
  if (loading) return <div className="p-6 text-brand-navy">Loading…</div>;
  if (!app) return <div className="p-6 text-red-700">{String(err) || "Not found."}</div>;

  const b = app.business || {};
  const canDecide = ["submitted", "info_requested"].includes(app.status);

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto" data-testid="admin-application-detail">
      <div className="mb-4">
        <Link to="/admin/membership-applications" className="text-xs text-brand-teal hover:underline">← Back to queue</Link>
      </div>

      <header className="mb-6 flex flex-col sm:flex-row sm:items-start justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-brand-teal font-semibold">Membership application</div>
          <h1 className="text-2xl font-bold text-brand-navy" data-testid="app-legal-name">{b.legal_name || "Unnamed"}</h1>
          <div className="text-sm text-gray-600">{b.trading_name}</div>
          <div className="text-xs text-gray-500 mt-1">Submitted {app.submitted_at ? new Date(app.submitted_at).toLocaleString("en-AU") : "—"}</div>
        </div>
        <div className="flex flex-col items-end gap-2">
          <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full bg-brand-teal/10 text-brand-teal`} data-testid="app-status-badge">
            {app.status.replace(/_/g, " ")}
          </span>
          {app.is_test && <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">Test</span>}
          {isOwner && app.is_test && (
            <button onClick={deleteTest} className="text-xs text-red-700 hover:underline" data-testid="delete-test-btn">Delete test application</button>
          )}
          {app.status === "info_requested" && (
            <button onClick={sendReminder} className="text-xs bg-amber-100 border border-amber-300 text-amber-800 px-3 py-1 rounded-full font-semibold hover:bg-amber-200" data-testid="send-reminder-btn">Send reminder email</button>
          )}
        </div>
      </header>

      {err && <div className="p-3 mb-4 rounded bg-red-50 border border-red-200 text-red-700 text-sm">{String(err)}</div>}

      {/* Compliance checklist */}
      <div className="grid sm:grid-cols-3 gap-3 mb-6" data-testid="compliance-checklist">
        <Check label="ABN — 11 digits" ok={(b.abn || "").length === 11} />
        <Check label="Licence(s)" ok={(app.licences || []).length > 0} />
        <Check label="Insurance(s)" ok={(app.insurances || []).length > 0} />
        <Check label={`Gallery ≥ 6 (${(app.gallery || []).length})`} ok={(app.gallery || []).length >= 6} />
        <Check label="Service postcodes" ok={((app.service || {}).postcodes || []).length > 0} />
        <Check label="Code of Conduct signed" ok={!!(app.conduct_acceptance || {}).accepted} />
      </div>

      <Section title="Business">
        <Field k="Legal name" v={b.legal_name} />
        <Field k="Trading name" v={b.trading_name} />
        <Field k="ABN" v={b.abn} />
        <Field k="Type" v={b.business_type} />
        <Field k="Address" v={`${b.address_line1 || ""}, ${b.suburb || ""} ${b.state || ""} ${b.postcode || ""}`} />
        <Field k="Phone" v={b.phone} />
        <Field k="Email" v={b.email} />
        <Field k="Year established" v={b.year_established} />
      </Section>

      <Section title={`Licences (${(app.licences || []).length})`}>
        {(app.licences || []).map((lic, i) => (
          <div key={lic.id || i} className="border border-brand-border p-2 rounded mb-2 text-sm" data-testid={`admin-licence-${i}`}>
            <div>#{lic.number} · {lic.state} · exp {lic.expiry}</div>
            {lic.file_url && <a href={lic.file_url} target="_blank" rel="noreferrer" className="text-xs text-brand-teal hover:underline">View certificate</a>}
          </div>
        ))}
      </Section>

      <Section title={`Insurance (${(app.insurances || []).length})`}>
        {(app.insurances || []).map((ins, i) => (
          <div key={ins.id || i} className="border border-brand-border p-2 rounded mb-2 text-sm" data-testid={`admin-insurance-${i}`}>
            <div>{ins.type.replace(/_/g, " ")} · {ins.insurer} · #{ins.policy_number} · ${(ins.coverage_amount || 0).toLocaleString()} · exp {ins.expiry}</div>
            {ins.file_url && <a href={ins.file_url} target="_blank" rel="noreferrer" className="text-xs text-brand-teal hover:underline">View certificate</a>}
          </div>
        ))}
      </Section>

      <Section title="Service coverage">
        <Field k="Primary state" v={app.service?.primary_state} />
        <Field k="Postcodes" v={(app.service?.postcodes || []).join(", ")} />
        <Field k="Job types" v={(app.service?.job_types || []).join(", ")} />
        <Field k="Min job size" v={app.service?.minimum_job_size_aud} />
      </Section>

      <Section title="Profile & gallery">
        <Field k="Slug" v={app.profile?.slug} />
        <Field k="Tagline" v={app.profile?.tagline} />
        <Field k="About" v={app.profile?.about} />
        <Field k="Team size" v={app.profile?.team_size} />
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mt-3">
          {(app.gallery || []).map((g, i) => (
            <div key={g.id || i} className="aspect-square rounded overflow-hidden bg-brand-cream border border-brand-border" data-testid={`admin-gallery-${i}`}>
              {g.url ? <img src={g.url} alt={g.title} className="w-full h-full object-cover" /> : <div className="text-[10px] p-1 text-gray-400">no image</div>}
            </div>
          ))}
        </div>
      </Section>

      <Section title={`References (${(app.references || []).length})`}>
        {(app.references || []).map((r, i) => (
          <div key={r.id || i} className="border border-brand-border p-2 rounded mb-2 text-sm">
            <div className="font-semibold">{r.name} — {r.suburb}</div>
            <div className="text-xs text-gray-700 italic">&ldquo;{r.testimonial}&rdquo;</div>
            <div className="text-[10px] text-gray-500">Consent confirmed: {r.consent_confirmed ? "yes" : "NO"}</div>
          </div>
        ))}
      </Section>

      <Section title="Code of Conduct & Tier">
        <Field k="Code accepted" v={app.conduct_acceptance?.accepted ? "yes" : "no"} />
        <Field k="Terms accepted" v={app.conduct_acceptance?.terms_accepted ? "yes" : "no"} />
        <Field k="Signature" v={app.conduct_acceptance?.signature_name} />
        <Field k="Signed at" v={app.conduct_acceptance?.signed_at} />
        <Field k="Signature IP" v={app.conduct_acceptance?.signature_ip} />
        <Field k="Tier" v={app.tier_selection?.tier_slug} />
        <Field k="Billing cycle" v={app.tier_selection?.billing_cycle} />
      </Section>

      {/* Decision panel */}
      {canDecide && (
        <div className="mt-6 border-2 border-brand-teal bg-white rounded-2xl p-4 sm:p-6" data-testid="decision-panel">
          <h2 className="text-lg font-bold text-brand-navy mb-3">Make a decision</h2>
          <div className="grid sm:grid-cols-2 gap-3 mb-3">
            {[
              { v: "approve", label: "Approve" },
              { v: "approve_with_conditions", label: "Approve with conditions" },
              { v: "request_info", label: "Request more info" },
              { v: "reject", label: "Reject" },
            ].map((o) => (
              <button
                key={o.v}
                onClick={() => setOutcome(o.v)}
                className={`px-3 py-2 rounded-lg text-sm font-semibold border ${outcome === o.v ? "bg-brand-navy text-white border-brand-navy" : "bg-white text-brand-navy border-brand-border hover:bg-brand-cream"}`}
                data-testid={`decision-${o.v}`}
              >
                {o.label}
              </button>
            ))}
          </div>

          {outcome === "approve_with_conditions" && (
            <div className="mb-3">
              <label className="block text-xs font-semibold uppercase mb-1">Conditions</label>
              <textarea value={conditions} onChange={(e) => setConditions(e.target.value)} rows={2} className="w-full border border-brand-border rounded px-2 py-1 text-sm" data-testid="decision-conditions" />
            </div>
          )}
          {outcome === "reject" && (
            <div className="mb-3 grid sm:grid-cols-2 gap-3">
              <label className="text-xs">
                <span className="font-semibold uppercase block mb-1">Rejection category</span>
                <select value={rejectionCategory} onChange={(e) => setRejectionCategory(e.target.value)} className="w-full border border-brand-border rounded px-2 py-1.5" data-testid="decision-rejection-category">
                  <option value="admin_or_info">Admin/info-fix (no cooldown)</option>
                  <option value="eligibility_or_standards">Eligibility or standards (30-day cooldown)</option>
                  <option value="fraud_or_misconduct">Fraud / serious misconduct (extended)</option>
                </select>
              </label>
              {rejectionCategory === "fraud_or_misconduct" && (
                <label className="text-xs">
                  <span className="font-semibold uppercase block mb-1">Restriction (days)</span>
                  <input type="number" value={reapplyDays} onChange={(e) => setReapplyDays(e.target.value)} className="w-full border border-brand-border rounded px-2 py-1.5" data-testid="decision-reapply-days" />
                </label>
              )}
            </div>
          )}
          {outcome === "request_info" && (
            <div className="mb-3">
              <label className="block text-xs font-semibold uppercase mb-1">Fields to re-open (comma separated)</label>
              <input value={infoFieldsRaw} onChange={(e) => setInfoFieldsRaw(e.target.value)} placeholder="e.g. licences, gallery, insurances" className="w-full border border-brand-border rounded px-2 py-1.5 text-sm" data-testid="decision-info-fields" />
            </div>
          )}

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold uppercase mb-1">Internal note (not visible to applicant)</label>
              <textarea value={internalNote} onChange={(e) => setInternalNote(e.target.value)} rows={2} className="w-full border border-brand-border rounded px-2 py-1 text-sm" data-testid="decision-internal-note" />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase mb-1">Message to applicant</label>
              <textarea value={applicantMessage} onChange={(e) => setApplicantMessage(e.target.value)} rows={2} className="w-full border border-brand-border rounded px-2 py-1 text-sm" data-testid="decision-applicant-message" />
            </div>
          </div>
          <div className="mt-3">
            <label className="block text-xs font-semibold uppercase mb-1">Reason (audit)</label>
            <input value={reason} onChange={(e) => setReason(e.target.value)} className="w-full border border-brand-border rounded px-2 py-1.5 text-sm" data-testid="decision-reason" />
          </div>
          <div className="mt-4 text-right">
            <button
              disabled={!outcome || saving}
              onClick={submitDecision}
              className="px-4 py-2 rounded-lg bg-brand-teal text-white font-semibold text-sm hover:bg-brand-teal/90 disabled:opacity-50"
              data-testid="decision-submit"
            >
              {saving ? "Saving…" : "Save decision"}
            </button>
          </div>
        </div>
      )}

      {/* Audit trail */}
      <div className="mt-6" data-testid="review-history">
        <h2 className="text-lg font-bold text-brand-navy mb-2">Review history</h2>
        {(app.review_history || []).length === 0 ? (
          <div className="text-sm text-gray-500 italic">No decisions yet.</div>
        ) : (
          <ol className="space-y-2">
            {(app.review_history || []).map((h, i) => (
              <li key={i} className="border-l-2 border-brand-teal pl-3 text-sm" data-testid={`history-${i}`}>
                <div className="text-xs text-gray-500">{new Date(h.at).toLocaleString("en-AU")} · {h.actor_email}</div>
                <div className="font-semibold">{h.action} → {h.to_status}</div>
                {h.reason && <div className="text-xs text-gray-700"><em>Reason:</em> {h.reason}</div>}
                {h.internal_note && <div className="text-xs text-gray-700"><em>Internal:</em> {h.internal_note}</div>}
                {h.applicant_message && <div className="text-xs text-gray-700"><em>To applicant:</em> {h.applicant_message}</div>}
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mb-6 border border-brand-border rounded-xl bg-white p-4">
      <h2 className="text-sm font-bold uppercase tracking-widest text-brand-teal mb-3">{title}</h2>
      <div className="grid sm:grid-cols-2 gap-2 text-sm">{children}</div>
    </section>
  );
}
function Field({ k, v }) {
  return (
    <div className="text-sm">
      <span className="text-xs uppercase tracking-widest text-gray-500 block">{k}</span>
      <span className="text-brand-navy font-medium break-words">{v ? String(v) : <em className="text-gray-400">—</em>}</span>
    </div>
  );
}
function Check({ label, ok }) {
  return (
    <div className={`p-2 rounded border text-xs font-semibold ${ok ? "border-brand-success/40 bg-brand-success/10 text-brand-success" : "border-red-200 bg-red-50 text-red-700"}`}>
      {ok ? "✓" : "✗"} {label}
    </div>
  );
}
